import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import z from "zod";

import { useTRPC } from "@/integrations/trpc/react";

import { Button } from "../base/button";
import { useAppForm } from "../base/form";
import { Modal } from "../base/modal";
import { title } from "../base/primitives";

export type EditDirectorProps = {
  directorId: number;
  name: string;
  email: string | null;
  phoneNumber: string | null;
  hidePhoneNumber: boolean;
};

const schema = z.object({
  email: z.string().trim().email().or(z.literal("")),
  phoneNumber: z.string().trim(),
  hidePhoneNumber: z.boolean(),
});

export function EditDirector({
  directorId,
  name,
  email,
  phoneNumber,
  hidePhoneNumber,
}: EditDirectorProps) {
  const [isOpen, setOpen] = useState(false);

  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason } = useMutation({
    ...trpc.directors.updateContact.mutationOptions(),
    onSuccess: () => {
      // The director row shows on both venue and tournament pages.
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());
      queryClient.invalidateQueries(trpc.venues.pathFilter());
      setOpen(false);
    },
  });

  const defaults = {
    email: email ?? "",
    phoneNumber: phoneNumber ?? "",
    hidePhoneNumber,
  };

  const form = useAppForm({
    defaultValues: defaults,
    validators: { onChange: schema },
    onSubmit: ({ value }) => mutate({ id: directorId, ...value }),
  });

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        // Re-sync to the latest props each open so a prior save (or an edit
        // from elsewhere) doesn't leave stale values in the form.
        if (open) form.reset(defaults);
        setOpen(open);
      }}
    >
      <Button
        variant="icon"
        color="alternate"
        radius="full"
        className="text-blue-500"
      >
        <PencilIcon size={16} />
      </Button>

      <Modal isDismissable isOpen={isOpen} onOpenChange={setOpen}>
        <div className="relative flex flex-col space-y-4 p-4">
          <Heading
            className={title({
              size: "sm",
              class: "flex flex-row justify-between items-center",
            })}
            slot="title"
          >
            <span>Edit {name}</span>

            <Button variant="icon" slot="close">
              <XIcon size={16} />
            </Button>
          </Heading>

          <p className="text-sm text-gray-500">
            Overrides what's shown on this director's tournaments and venue.
            Notifications still go to their account email.
          </p>

          <form
            className="flex flex-col space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {failureReason && (
              <form.AppForm>
                <form.Alert
                  title="Uh oh!"
                  description={failureReason.message}
                />
              </form.AppForm>
            )}

            <form.AppField
              name="email"
              children={(field) => (
                <field.Text
                  field={field}
                  type="email"
                  label="Display email"
                  placeholder="Leave blank to use their account email"
                />
              )}
            />

            <form.AppField
              name="phoneNumber"
              children={(field) => (
                <field.Text
                  field={field}
                  type="tel"
                  label="Display phone"
                  placeholder="Leave blank to use their account phone"
                />
              )}
            />

            <form.AppField
              name="hidePhoneNumber"
              children={(field) => (
                <field.Checkbox
                  field={field}
                  label="Hide phone number entirely"
                />
              )}
            />

            <form.AppForm>
              <form.Footer className="justify-end">
                <Button onPress={() => setOpen(false)}>Cancel</Button>
                <form.SubmitButton>Save</form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </DialogTrigger>
  );
}
